const isArray = require('lodash/isArray')
/*
  Prepares a where clause out of the supplied filters, providing the clause text
  and an array of values to supply to the query engine
  e.g. {a: 1, b: 'two'}
  returns {whereText: 'WHERE "a"=$1 AND "b"=$2', whereValues: [1,'two']}

  `preparers` is an optional map of functions to make a custom statement for a given property.
  a preparer function should return an array of statements (can be more than one). The default
  preparer is (key, value) => [{statement: `"${key}"=%INDEX%`, value}]

  A statement's contract with this function is its `%INDEX%` markers:
    - write `%INDEX%` where the statement needs its value, as many times as it needs it
      (every marker in one statement resolves to the same positional argument)
    - leave `%INDEX%` out for a statement that binds nothing, e.g. `"a" IS NULL` — no
      positional argument is spent, so the statements after it keep their numbering
    - a statement asking for a value must have one: a filter value of `undefined` throws.
      `null` is a value and gets bound, so a statement that means `IS NULL` has to say so
      without a marker
  Positional arguments are handed out in statement order, so a statement never has to know
  what the filters around it did.

  `search` is an optional structure defining a free text search to perform including `text` to search
  for `columns[]` to look in

  `positionalArgumentOffset` shifts the numbering for callers that bind values before the where
  clause, e.g. the SET list of an UPDATE.
*/
const defaultPreparer = (key, value) => {
  if (Array.isArray(value)) {
    return [{statement: `"${key}" = ANY(%INDEX%)`, value}]
  }
  if (value === null) {
    return [{statement: `"${key}" IS NULL`, value}]
  }
  return [{statement: `"${key}"=%INDEX%`, value}]
}

// Every clause carries a `label` for what asked for it, so a missing value names its filter — or
// the search — rather than only the statement it was going to fill.
const searchToClause = search => ({
  label: 'the search clause',
  statement: '(' + search.columns
    .map(c => `"${c}" ILIKE '%'||%INDEX%||'%'`)
    .join(' OR ') + ')',
  value: search.text
})

const filterToClauses = (filter, preparers) =>
  Object.keys(filter || {}).reduce((clauses, field) => {
    const preparer = preparers && typeof preparers[field] === 'function' ? preparers[field] : defaultPreparer
    return clauses.concat(
      preparer(field, filter[field]).map(clause => ({...clause, label: `filter "${field}"`}))
    )
  }, [])

// A statement asking for a value the filter didn't supply has no honest reading, so this refuses
// to guess. Dropping the clause would be the tempting guess, and it is the dangerous one: the
// DELETE and UPDATE built on this clause (`deleteWhere`, `updateWhere`) would then touch every row
// the dropped condition was there to exclude. Binding nothing is no better — it leaves a
// placeholder with no argument behind it.
//
// Nothing can be relying on the behaviour this replaces: that emitted the placeholder anyway, so
// the argument count never matched and Postgres rejected the query every time.
const assertValuePresent = ({statement, value, label}) => {
  if (value === undefined) {
    throw new Error(`getWhereClause: ${label} has no value for statement \`${statement}\``)
  }
}

const bindClause = ({statements, values}, clause, positionalArgumentOffset) => {
  // An empty statement is a preparer saying there is nothing to filter on. Its value goes with it:
  // keeping one for a statement that never made it into the clause is what threw the numbering off
  // before.
  if (!clause.statement) {
    return {statements, values}
  }
  if (!clause.statement.includes('%INDEX%')) {
    return {statements: statements.concat(clause.statement), values}
  }
  assertValuePresent(clause)
  const positionalArgument = positionalArgumentOffset + values.length + 1
  return {
    statements: statements.concat(clause.statement.replace(/%INDEX%/g, `$${positionalArgument}`)),
    values: values.concat([clause.value])
  }
}

const getWhereClause = ({filter, preparers, search, positionalArgumentOffset, includeWhereKeyword = true}) => {
  const offset = positionalArgumentOffset || 0
  const clauses = filterToClauses(filter, preparers).concat(search ? [searchToClause(search)] : [])
  const {statements, values} = clauses.reduce(
    (acc, clause) => bindClause(acc, clause, offset),
    {statements: [], values: []}
  )

  const whereText = statements.length
    ? (includeWhereKeyword ? 'WHERE ' : '') + statements.join(` AND `)
    : ``

  return { whereText, whereValues: values }
}

const getOrderByClause = (ordering, columns, table) => {
  if (!ordering) {
    return ''
  }

  const tablePrefix = table ? `${table}.` : ''
  const orderings = Array.isArray(ordering) ? ordering : [ordering]

  const sortExpressions = orderings.map(order => {
    const sortExpression = order.startsWith('-') ? order.replace('-', tablePrefix) : `${tablePrefix}${order}`
    const sortDirection = order.startsWith('-') ? 'DESC' : 'ASC'

    if (columns && columns.length && !(columns.includes(sortExpression) || columns.includes(sortExpression.replace(tablePrefix, '')))) {
      throw new Error(`sortExpression ${sortExpression} is not an available sort column. Available columns: ${columns.join(', ')}`)
    }

    return `${sortExpression} ${sortDirection}`
  })

  const sortText = `ORDER BY ${sortExpressions.join(', ')}`
  return sortText
}

const mapInserts = (insert) => {
  const fields = Object.keys(insert[0])

  const flattened = insert.reduce(
    (acc, row) => acc.concat(fields.map(field => row[field])),
    []
  )
  const dollarSigns = insert
    .map((_, i) => {
      const rowDollarSigns = `${fields.map(
        (__, j) => `$${i * fields.length + j + 1}`
      )}`
      return `(${rowDollarSigns})`
    })
    .join(', ')

  return {
    fields,
    flattened,
    dollarSigns
  }
}

const getInsertProperties = (rows, columns = null, startIndex = 0, columnTypes = {}) => {
  if (!Array.isArray(rows)) {
    throw new Error('Rows should be an array')
  }
  if (rows.length === 0) {
    return { columns: null, values: [], placeholders: null }
  }
  columns = columns || Object.keys(rows[0])
  // flatten all rows into single values array in columns order
  const values = [].concat(
    ...rows.map(r => columns.map(c => r[c]))
  )
  // create placeholders for each row, like ($1, $2, ...), ($1, $2, ...)
  const placeholders = rows.map((_, i) =>
    '(' + columns.map((_, j) => `$${startIndex + i * columns.length + j + 1}${columnTypes ? columnTypes[_] ? `::${columnTypes[_]}` : '' : ''}`).join(', ') + ')'
  ).join(', ')

  return { columns: columns.join(', '), values, placeholders }
}

const getPositionalArgs = (params, offset = 1) => params.map((_, key) => `$${key + offset}`).join(',')

const getListArgs = (params, offset = 1, tag = '') => {
  let nextOffset = offset
  const args = params.map((_, key) => {
    nextOffset = key + offset
    return `$${nextOffset}${tag}`
  }
  ).join(' , ')
  return { args, nextOffset: params.length ? nextOffset + 1 : nextOffset }
}

/**
 * Generates an SQL string for filtering values based on the given array of columns and table name.
 *
 * @param {string[]} columns - An array of column names for which filtering values are needed.
 * @param {string} tableName - The name of the table from which to fetch data.
 * @param {string} preFilter - A pre-filter to be used as a basis i.e. we first apply it and then get the filtering values
 * @returns {string} JSON string with the corresponding filtering values indexed by column names e.g.:
 *
SELECT
    jsonb_build_object(
        'created_at', json_agg(DISTINCT data_shipment.created_at),
        'origin_id', json_agg(DISTINCT data_shipment.origin_id),
        ...
    ) AS "values"
FROM
    avocado.data_shipment
where
    data_shipment.created_at between '2020-01-01'
    AND '2020-04-01'
*/
const getColumnsUniqueValuesQuery = (tableExpression, columns, calculatedColumns = [], preFilter, preparers) => {
  const preFilterWhereClause = getWhereClause({filter: preFilter, preparers, includeWhereKeyword: false})

  const columnDefinitions = calculatedColumns
    .concat(columns.map(column => { return {name: column, expression: column} }))

  let sql = `
  SELECT
    jsonb_build_object(
      ${columnDefinitions.map(columnDefinition => `'${columnDefinition.name}', json_agg(DISTINCT ${columnDefinition.expression})`).join(',')}
    ) AS "values"
  FROM
    ${tableExpression}
  WHERE ${preFilterWhereClause.whereText}
`
  return { query: sql, params: preFilterWhereClause.whereValues }
}

const arrayOrStringPreparer = (key, value) => {
  let arrayValue = isArray(value) ? value : [value]

  // Handle null values - generate "IS NULL" without parameter placeholder
  // This covers both null and [null] cases
  if (arrayValue.length === 1 && arrayValue[0] === null) {
    return [{ statement: `"${key}" IS NULL`, value: null }]
  }

  // We are now sure we have an array of values, if we have just one value, we use equal to
  // If more than one we use ANY
  if (arrayValue && arrayValue.length === 1) {
    const onlyValue = arrayValue[0]
    return [
      { statement: `"${key}"=%INDEX%`, value: onlyValue }
    ]
  }

  return [
    { statement: `"${key}" = ANY(%INDEX%::TEXT[])`, value: arrayValue }
  ]
}

// Returns a preparer that qualifies the column with `tableName` — use this when
// the query JOINs another table that has a column with the same name, to avoid
// "column reference is ambiguous" errors.
const qualifiedArrayOrStringPreparer = (tableName) => (key, value) => {
  const col = `${tableName}."${key}"`
  const arrayValue = isArray(value) ? value : [value]
  if (arrayValue.length === 1 && arrayValue[0] === null) {
    return [{ statement: `${col} IS NULL`, value: null }]
  }
  if (arrayValue.length === 1) {
    return [{ statement: `${col}=%INDEX%`, value: arrayValue[0] }]
  }
  return [{ statement: `${col} = ANY(%INDEX%::TEXT[])`, value: arrayValue }]
}

// A bare key (e.g. 'origin_id') is quoted so PostgreSQL resolves the column;
// a qualified ref (e.g. 's.origin_id') is emitted as-is so the same preparer
// can serve queries that alias their table (the LMD download query uses `s`).
const quoteColumn = (key) => (key.includes('.') ? key : `"${key}"`)

const matchAllValuesILikePreparer = (key, value) => {
  let arrayValue = isArray(value) ? value : [value]
  const column = quoteColumn(key)
  // We are now sure we have an array of values, if we have just one value, we just use ILIKE
  // If more than one we use ILIKE ALL
  if (arrayValue && arrayValue.length === 1) {
    const onlyValue = arrayValue[0]
    return [
      { statement: `${column} ILIKE '%'||%INDEX%||'%'`, value: onlyValue }
    ]
  }

  return [
    { statement: `${column} ILIKE ALL(%INDEX%::TEXT[])`, value: arrayValue }
  ]
}

// Like matchAllValuesILikePreparer but with OR semantics for multiple values:
// the column must match ANY of the supplied patterns (not all of them).
// Use this when a single column can hold one of several alternative values,
// e.g. the funder-split virtual warehouse ids a selected LMD source
// warehouse expands to (issue #23868).
// The wildcards are baked into each array element since `ILIKE ANY(array)`
// cannot wrap them in SQL the way the single-value branch does.
const matchAnyValueILikePreparer = (key, value) => {
  let arrayValue = isArray(value) ? value : [value]
  const column = quoteColumn(key)
  if (arrayValue && arrayValue.length === 1) {
    const onlyValue = arrayValue[0]
    return [
      { statement: `${column} ILIKE '%'||%INDEX%||'%'`, value: onlyValue }
    ]
  }

  return [
    { statement: `${column} ILIKE ANY(%INDEX%::TEXT[])`, value: arrayValue.map(v => `%${v}%`) }
  ]
}

const getFilter = (query, limit = 20) => {
  const parsedPage = parseInt(`${query.page}`)
  return {
    offset: query.page && Number.isInteger(parsedPage) && parsedPage > 0 ? ` offset ${(parsedPage - 1) * limit} ` : '',
    limit,
    page: query.page && Number.isInteger(parsedPage) && parsedPage > 0 ? parsedPage : 1
  }
}

/**
 * @param {Object} params
 * @param {string} params.baseQuery
 * @param {string} params.cteName
 * @param {string} params.orderByClause
 * @param {number} params.pageNo
 * @param {number} params.maxLimit
 * @returns {string}
 */
const queryPaginationTransform = ({
  baseQuery,
  cteName,
  orderByClause,
  pageNo,
  maxLimit
}) => {
  const { limit, offset, page } = getFilter({ page: pageNo }, maxLimit)
  return `
    ${baseQuery},
    list as (
      select * from ${cteName}
      ${orderByClause}
      limit ${limit} ${offset}
    )
    select
      coalesce(
        (
          select jsonb_agg(p)
          from (
            select * from list
            ${orderByClause}
          ) p
        ),
        '[]'::jsonb
      ) as data,
      json_build_object(
        'page', ${page},
        'page_total', ceil(coalesce( (select count(1) from ${cteName}) , 1) / ${limit}::float ),
        'per_page', ${limit},
        'current', coalesce((select count(1) from list), 0),
        'total', coalesce((select count(1) from ${cteName}), 0)
      ) as paging
    `
}

module.exports = {
  getWhereClause,
  getOrderByClause,
  mapInserts,
  getInsertProperties,
  getPositionalArgs,
  getListArgs,
  getColumnsUniqueValuesQuery,
  matchAllValuesILikePreparer,
  matchAnyValueILikePreparer,
  arrayOrStringPreparer,
  qualifiedArrayOrStringPreparer,
  queryPaginationTransform
}
